// libs
import React, { memo, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';

// global components
import ToastProvider from 'shared/components/Toast';

// components
import Title from 'shared/layouts/DetailsPage/components/ProductInfo/components/Title';
import AboutDesigner from 'shared/layouts/DetailsPage/components/ProductInfo/components/AboutDesigner';
import CustomerReview from 'shared/layouts/DetailsPage/components/ProductInfo/components/CustomerReview';
import SizeOptions from 'shared/layouts/DetailsPage/components/ProductInfo/components/SizeOptions';
import ColorSwatches from 'shared/layouts/DetailsPage/components/ProductInfo/components/ColorSwatches/Desktop';
import ActionContainer from 'shared/layouts/DetailsPage/components/ProductInfo/components/ActionContainer';
import AddToBag from 'shared/layouts/DetailsPage/components/ProductInfo/components/AddToBag';
import Notify from 'shared/components/Notify/Desktop';
import PriceWithDiscount from 'shared/layouts/DetailsPage/components/PriceWithDiscount';
import ProductOffers from 'shared/layouts/DetailsPage/components/ProductInfo/components/ProductOffers';
import DisclaimerText from 'shared/layouts/DetailsPage/components/ProductInfo/components/DisclaimerText';
import Multitag from 'shared/layouts/DetailsPage/components/ProductInfo/components/Multitag';
import ProductDescription from 'shared/layouts/DetailsPage/components/ProductInfo/components/ProductDescription';
import EcomPolicy from './components/EcomPolicy';
import PriceReveal from 'shared/layouts/DetailsPage/components/ProductInfo/components/PriceReveal/Desktop';
import WishlistButton from 'shared/layouts/DetailsPage/components/ProductInfo/Desktop/components/WishlistButton';
import RatingSummary from './components/RatingSummary';
import NewEcomPolicy from './components/NewEcomPolicy';

// hooks
import { useProductInfo } from 'shared/layouts/DetailsPage/components/ProductInfo/hooks';

// selectors
import { useSelectedSKUCode } from 'shared/store/details/hooks/selectors';

// styles
import {
  Actions,
  Container,
  CustomModelWidget,
  CustomWishlist,
  OutOfStockHelperText,
  PrimaryContent,
  Separator20,
  ProductOffersContainer,
  PromotionText,
} from './style';

// constants
import { USER_TYPE } from 'shared/layouts/DetailsPage/components/ProductInfo/constants';
import { TEXTS } from 'shared/components/Notify/constants';
const WISHLIST_ICON_SIZE = 24;
const CONTAINER_HEIGHTS = {
  COUPONS_OFFERS: 340,
  COUPONS: 164,
  OFFERS: 136,
  MARGIN: 40,
};

// analytics
import { trackNotifyMeClicked } from 'shared/layouts/Wishlist/analytics';

// defs
import { TagList } from 'shared/store/details/types';
import { CouponDetails, OfferDetails } from './types';
import { ITrackPrams } from '../Mobile/types';
interface IProductInfoProps {
  brandUrl: string;
  productID: string;
  badge?: ProductDetail.IBadge;
  disclaimer?: string;
  onIntlNetwork: boolean;
  priceRevealConfig: ProductDetail.IPriceRevealConfig;
  type: string;
  categoryName: string;
  isOneSize: boolean;
  colorOptions: ProductDetail.IColorOption[];
  defaultColor: ProductDetail.IColor | null;
  isWishlistEnabled: boolean;
  tags: TagList;
  categoryLevels: {
    [key: string]: { id: string; name: string };
  };
  isProductOffersAvailable: boolean;
  productOffers: {
    offers: CouponDetails[];
    coupons: OfferDetails[];
  };
  isOffersFetching: boolean;
  newEcomFetchApiEnabled: boolean;
}

function ProductInfoDesktop({
  brandUrl,
  productID,
  badge,
  disclaimer,
  onIntlNetwork,
  priceRevealConfig,
  type,
  categoryName,
  isOneSize,
  colorOptions,
  defaultColor,
  isWishlistEnabled,
  tags,
  categoryLevels,
  isProductOffersAvailable,
  productOffers = { offers: [], coupons: [] },
  isOffersFetching,
  newEcomFetchApiEnabled,
}: IProductInfoProps) {
  const {
    isAddedToCart,
    isOutOfStock,
    displayTitle,
    displaySubTitle,
    displayPrice,
    displayDiscount,
    displayDiscountedPrice,
    setSelectedSize,
    selectedSize,
    addToCart,
    priceRevealData,
    isSalePriceLive,
  } = useProductInfo();

  const productSKU = useSelectedSKUCode();
  const [shakeSizeSelector, setShakeSizeSelector] = useState(false);

  const nudgeSizeSelector = () => {
    setShakeSizeSelector(false);
    setTimeout(() => {
      setShakeSizeSelector(true);
    }, 0);
  };
  const handleOnNotified = ({ email = "" }: ITrackPrams) => {
    trackNotifyMeClicked({
      email,
      productId: productID,
      price: String(displayPrice),
      isOutOfStock: true,
      discountedPrice: displayDiscountedPrice,
      discount: displayDiscount,
      type,
      categoryLevels,
      variantType: selectedSize?.sizeName,
      variantProductID: selectedSize?.id,
      productData: selectedSize,
      ...(badge?.title ? { productBadge: badge.title } : {}),
    });
  };

  const getAddToCartAction = useMemo(() => {
    return selectedSize === undefined ? nudgeSizeSelector : addToCart;
  }, [addToCart, selectedSize]);

  const selectedProductID = selectedSize !== undefined ? selectedSize.id : productID;
  const userType = onIntlNetwork ? USER_TYPE.INTERNATIONAL : USER_TYPE.DOMESTIC;

  const hasColors = isEmpty(defaultColor) === false && isEmpty(colorOptions) === false;

  const isPriceReveal = priceRevealConfig?.desktop && priceRevealData;

  const showPromotionText =
    priceRevealConfig?.desktop && priceRevealConfig?.salePromotionConfig.desktop && isSalePriceLive;

  const showPriceDetailsSeperator = hasColors || !isOneSize;

  const showColorsSizesSeperator = hasColors && !isOneSize;

  const wishlistMetaData = {
    productID: productID,
    categoryName: categoryName,
    discountPercent: displayDiscount,
    MRP: displayPrice,
    productName: displaySubTitle,
    productSKU: productSKU,
    discountedPrice: displayDiscountedPrice,
    productBrandName: displayTitle,
    isOutOfStock: Boolean(isOutOfStock),
    productTags: tags,
    categoryLevels,
    isPriceReveal: priceRevealConfig?.desktop ? !isEmpty(priceRevealData) : undefined,
    isSalePriceLive: showPromotionText,
    isProductOffersAvailable,
    ...(type ? { productType: type } : {}),
    ...(badge?.title ? { productBadge: badge.title } : {}),
  };

  const wishlistComponent = (
    <CustomWishlist
      size={WISHLIST_ICON_SIZE}
      productId={productID}
      canShowNotification={true}
      CustomWishlist={WishlistButton}
      trackingMetaData={wishlistMetaData}
    />
  );

  const offersContainerHeight = useMemo(() => {
    if (__SERVER__ || isOffersFetching) return CONTAINER_HEIGHTS.COUPONS_OFFERS;

    let height = 0;

    // container height for coupons
    if (productOffers.coupons?.length) {
      height += CONTAINER_HEIGHTS.COUPONS;
    }

    // add margin for whenever there are both coupons and offers
    if (productOffers.coupons?.length && productOffers.offers?.length) {
      height += CONTAINER_HEIGHTS.MARGIN;
    }

    // container height for offers
    if (productOffers.offers?.length) {
      height += CONTAINER_HEIGHTS.OFFERS;
    }

    return height;
  }, [productOffers, isOffersFetching]);

  return (
    <Container
      showColorsSizesSeperator={showColorsSizesSeperator}
      isWishlistEnabled={isWishlistEnabled}
    >
      <ToastProvider>
        <PrimaryContent>
          <Multitag badge={badge} />
          <Title title={displayTitle} subTitle={displaySubTitle} actionUrl={brandUrl} />

          <RatingSummary />
          <PriceWithDiscount
            price={displayPrice}
            discount={displayDiscount}
            discountedPrice={displayDiscountedPrice}
          />

          {!isOutOfStock && isPriceReveal && (
            <PriceReveal
              priceRevealData={priceRevealData}
              priceRevealConfig={priceRevealConfig.pdp}
              productId={productID}
              wishlistMetaData={wishlistMetaData}
            />
          )}

          {showPromotionText && (
            <PromotionText promotionTextConfig={priceRevealConfig.salePromotionConfig.pdp} />
          )}
          {showPriceDetailsSeperator && <Separator20 />}

          <ColorSwatches />

          {showColorsSizesSeperator && <Separator20 />}

          <SizeOptions
            onSelect={setSelectedSize}
            currentSize={selectedSize}
            shake={shakeSizeSelector}
            isMobile={false}
          />

          <DisclaimerText disclaimer={disclaimer} data-test="pdp-disclaimer-text" />

          <CustomModelWidget />
          {isOutOfStock ? (
            <ActionContainer>
              <Actions>
                {wishlistComponent}
                <Notify productId={selectedProductID} onNotified={handleOnNotified} />
              </Actions>
              <OutOfStockHelperText>{TEXTS.NO_STOCK_MESSAGE}</OutOfStockHelperText>
            </ActionContainer>
          ) : (
            <ActionContainer>
              <Actions>
                {wishlistComponent}
                <AddToBag
                  selectedSize={selectedSize}
                  isAddedToCart={isAddedToCart}
                  onClick={getAddToCartAction}
                  wishlistEnabled={false}
                  disabledOverride={true}
                />
              </Actions>
            </ActionContainer>
          )}

          {!isOutOfStock &&
            (userType === USER_TYPE.DOMESTIC && newEcomFetchApiEnabled ? (
              <NewEcomPolicy />
            ) : (
              <EcomPolicy />
            ))}

          <ProductOffersContainer height={offersContainerHeight}>
            <ProductOffers isDesktop />
          </ProductOffersContainer>
        </PrimaryContent>
        <ProductDescription />
        <CustomerReview />
        <AboutDesigner />
      </ToastProvider>
    </Container>
  );
}

export default memo(ProductInfoDesktop);
